import { useEffect, useState } from 'react';
import type { MenuPulse, MenuPulseLocale, MenuPulseLocationSystem } from '../../../server/routes/menu-pulse';
import { useNewLocationSystemEnabled } from '../../../utils/facets/new-location-system-dev-flag';

/** Matches server/utils/nav-pulse.ts's MENU_PULSE_CACHE_TTL_MS: once a value (SSR-seeded or
 * client-fetched) is this old, the next render triggers a refetch instead of reusing it forever
 * for the page's lifetime (TEC navbar redesign Phase 3 item 3.2). */
const CLIENT_CACHE_TTL_MS = 10 * 60 * 1000;

interface PulseCacheEntry {
  value: MenuPulse | null;
  /** When this value was known-fresh: the pulse's own `updatedAt` for an SSR-seeded value (so a
   * value that was already a few minutes old when the page rendered gets refetched on schedule,
   * not ten minutes from page load), or the client fetch's completion time otherwise. */
  fetchedAt: number;
}

/** Per-page-load cache so reopening the menu never refetches within the TTL. Keyed by locale and
 * location system, shared by every consumer of this hook (IndexStrip, MobileIndexSubtitle,
 * ValueSheet, MobileMenuModal). */
const pulseCache = new Map<string, PulseCacheEntry>();
const inflight = new Map<string, Promise<MenuPulse | null>>();

function cacheKey(locale: MenuPulseLocale, system: MenuPulseLocationSystem): string {
  return `${locale}:${system}`;
}

function isFresh(entry: PulseCacheEntry | undefined, now: number): entry is PulseCacheEntry {
  return !!entry && now - entry.fetchedAt < CLIENT_CACHE_TTL_MS;
}

/** Exported for tests: the request URL carries the TEC-482 version so the edge cache keeps the two apart. */
export function buildMenuPulseUrl(locale: MenuPulseLocale, system: MenuPulseLocationSystem): string {
  return `/api/menu-pulse?locale=${locale}&ls=${system}`;
}

function loadMenuPulse(locale: MenuPulseLocale, system: MenuPulseLocationSystem): Promise<MenuPulse | null> {
  const key = cacheKey(locale, system);
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = fetch(buildMenuPulseUrl(locale, system), { headers: { Accept: 'application/json' } })
    .then(async (res) => (res.ok ? ((await res.json()) as { pulse: MenuPulse | null }).pulse : null))
    .catch(() => null)
    .then((pulse) => {
      pulseCache.set(key, { value: pulse, fetchedAt: Date.now() });
      inflight.delete(key);
      return pulse;
    });
  inflight.set(key, request);
  return request;
}

/**
 * Live market figures for the menu's Explore rows and the navbar live-index strip (open jobs,
 * remote jobs, employers hiring, new today). Fetched once per page load, and only after the menu
 * has actually been opened (`enabled`): except the strip, which passes `enabled: true` since
 * it's always visible.
 *
 * TEC navbar redesign Phase 3 item 3.2: pass `initialPulse` (the SSR-rendered value from
 * `pageContext.navPulse`, seeded in server/vike-handler.ts from the same shared cache
 * server/utils/nav-pulse.ts exposes) so the first render already has numbers instead of starting
 * empty: this is what removes the client-fetch-on-first-load layout shift the Phase 1 spec
 * flagged. Omit it (or pass undefined) for consumers that mount later (the value sheet, the
 * mobile menu modal), which keep the original "fetch when enabled" behaviour unchanged.
 * Whichever value is showing (SSR-seeded or client-fetched), it is only refetched once it's at
 * least ten minutes old: matching the server cache's own TTL: not on every mount.
 */
export function useMenuPulse(
  locale: MenuPulseLocale,
  enabled: boolean,
  initialPulse?: MenuPulse | null,
): MenuPulse | null {
  const system: MenuPulseLocationSystem = useNewLocationSystemEnabled() ? 'new' : 'legacy';
  const key = cacheKey(locale, system);

  const [pulse, setPulse] = useState<MenuPulse | null>(() => {
    // pulseCache is a module-level singleton, which only means "one per page load" in the
    // browser. On the server it's "one per warm Lambda container," so it survives across
    // unrelated requests. Reading or writing it during SSR would leak one visitor's pulse into
    // another's HTML (and mismatch the initialPulse this request actually computed), so SSR
    // always uses this request's own initialPulse untouched by the cache.
    if (typeof window === 'undefined') return initialPulse ?? null;

    const existing = pulseCache.get(key);
    if (existing) return existing.value;
    // Only a real server value seeds the cache. A null (SSR timed out, or a renderer whose
    // pageContext carries no pulse, e.g. the SRP island) must leave the cache empty so the
    // effect below fetches; seeding null used to mark the key fresh and blank the strip for
    // ten minutes.
    if (!initialPulse) return null;
    // Seed the shared cache from the SSR value exactly once (lazy useState initializer runs at
    // most once per mount), so every other consumer on the same page reuses it too instead of
    // each independently fetching on mount.
    const parsedUpdatedAt = initialPulse ? Date.parse(initialPulse.updatedAt) : NaN;
    const fetchedAt = Number.isNaN(parsedUpdatedAt) ? Date.now() : parsedUpdatedAt;
    pulseCache.set(key, { value: initialPulse, fetchedAt });
    return initialPulse;
  });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const entry = pulseCache.get(key);
    if (isFresh(entry, Date.now())) {
      setPulse(entry.value);
      return;
    }
    loadMenuPulse(locale, system).then((value) => {
      if (!cancelled) setPulse(value);
    });
    return () => {
      cancelled = true;
    };
  }, [locale, system, enabled, key]);

  return pulse;
}
